import React, { FC, Fragment, ReactNode, useEffect, useState } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { Badge, Box, Button, Flex, IconButton, Skeleton } from '@chakra-ui/react';
import { MdClose, MdInfo, MdPhotoCamera, MdShare } from 'react-icons/md';
import moment from 'moment';

import { useAlert, useApi } from '@app/hooks';
import { SharedPhotoDescriptor } from '@app/api/share';

import {ImageDescriptor} from '../../../api/models/image';
import { useShare } from '../../../hooks/react-native/use-share';
import FieldView from '../field-view';
import Dialog from './dialog';
import { USER_DATE_TIME_FORMAT } from '../../../utils/constants';
import { PanZoomPicture } from '../pan-zoom-picture';
import { Txt } from '../text';

const defaultPhotoData =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' version='1' width='50vw' height='50vh' viewBox='-50 -50 126 126' enable-background='new 0 0 26 26'%3E%3Cpath style='text-indent:0;text-align:start;line-height:normal;text-transform:none;block-progression:tb;-inkscape-font-specification:Bitstream Vera Sans' fill='gray' d='M 0.90625 -0.03125 A 1.0001 1.0001 0 0 0 0.78125 0 A 1.0001 1.0001 0 0 0 0.28125 1.71875 L 24.28125 25.71875 A 1.016466 1.016466 0 1 0 25.71875 24.28125 L 23.40625 21.96875 C 24.903411 21.780503 26 20.562191 26 19 L 26 7 C 26 5.3 24.7 4 23 4 L 18.46875 4 L 17.5 2.09375 C 17.2 1.39375 16.4875 1 15.6875 1 L 10.1875 1 C 9.4875 1 8.80625 1.39375 8.40625 2.09375 L 7.46875 4 L 5.4375 4 L 1.71875 0.28125 A 1.0001 1.0001 0 0 0 0.90625 -0.03125 z M 0.8125 5 C 0.3125 5.5 1.4802974e-16 6.2 0 7 L 0 19 C 0 20.7 1.3 22 3 22 L 17.8125 22 L 15.40625 19.59375 C 14.60625 19.79375 13.8 20 13 20 C 9.1 20 6 16.9 6 13 C 6 12.2 6.20625 11.39375 6.40625 10.59375 L 0.8125 5 z M 13 6 C 16.9 6 20 9.1 20 13 C 20 14.607299 19.46337 16.007892 18.59375 17.15625 L 17.15625 15.71875 C 17.667869 14.932714 18 14.01236 18 13 C 18 10.2 15.8 8 13 8 C 11.98764 8 11.067286 8.3321309 10.28125 8.84375 L 8.84375 7.40625 C 10.015185 6.5366302 11.462584 6 13 6 z M 8 12.1875 L 8 13 C 8 15.8 10.2 18 13 18 C 13.3 18 13.4875 18.00625 13.6875 17.90625 L 8 12.1875 z' overflow='visible' enable-background='accumulate' font-family='Bitstream Vera Sans'/%3E%3C/svg%3E";

interface GalleryModalProps {
  images: Array<ImageDescriptor>;
  title: string;
  additional?: ReactNode;
  icon?: ReactNode;
  open?: boolean;
  onClose?: () => void;
  index?: number;
}

const GalleryModal: FC<GalleryModalProps> = (props) => {
  const { title, icon, onClose, index } = props;
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState<Array<ImageDescriptor>>(props.images || []);
  const { success } = useAlert();
  const api = useApi();

  const [currentImage, setCurrentImage] = useState<number>(index || 0);

  useEffect(() => {
    setOpen(props.open);
  }, [props.open]);

  const onChange = async (_: number, { currentSlide }: { currentSlide: number }) => {
    if (!images[currentSlide]?.image_pid) {return;}
    if (images[currentSlide].src) {return;}
    setCurrentImage(currentSlide);
    api.file
      .get('' + images[currentSlide].image_pid)
      .then((response) => {
        setImages([
          ...images.slice(0, currentSlide),
          {
            ...images[currentSlide],
            src:
              'data:image/jpeg;base64,' +
              response,
          },
          ...images.slice(currentSlide + 1),
        ]);
      })
      .catch(() => {
        setImages([
          ...images.slice(0, currentSlide),
          { ...images[currentSlide], src: defaultPhotoData },
          ...images.slice(currentSlide + 1),
        ]);
      });
      if(images[currentSlide].hash) {
        const info = await api.share.getPhotoShare(images[currentSlide].hash);
        if (info.code === 404) {
          //setNotFound(true);
        } else {
          setExtraInfo(info.data);
        }
      }
    
  };

  useEffect(() => {
    if (!images[0]?.image_pid) {return;}
    if (!open) {
      onClose?.();
      return;
    }
    if (images[0]?.src) {return;}
    api.file
      .get('' + images[0].image_pid)
      .then((response) => {
        setImages([
          {
            ...images[0],
            src:
              'data:image/jpeg;base64,' +
              response,
          },
          ...images.slice(1),
        ]);
      })
      .catch((e) => {
        setImages([
          { ...images[0], src: defaultPhotoData },
          ...images.slice(1),
        ]);
      });
      if(images[0]?.hash) {
        (async () => {
          const info = await api.share.getPhotoShare(images[0].hash);
          if (info.code === 404) {
            //setNotFound(true);
          } else {
            setExtraInfo(info.data);
          }
        })();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setImages(props.images);
  }, [props.images]);

  const share = useShare();

  const shareToClipboard = (hash: string) => {
    const url = `${window.location.origin}/share/#${hash}`;

    if (share) {
      share({ title: 'Фото из системы SNEG:', url });
      return;
    }

    if (navigator.share) {
      navigator.share({
        title: 'SNEG: Photo',
        url,
      });
      return;
    }

    navigator.clipboard.writeText(url);
    success('Ссылка на файл скопирована');
  };

  // additions 
  const [openExtra, setOpenExtra] = useState(false);
  const [extraInfo, setExtraInfo] = useState<SharedPhotoDescriptor>();

  return (
    <>
      {icon ? ( 
        <>{icon}</>
      ) : (<Box position='relative'>
        <IconButton
          onClick={() => images.length > 0 && setOpen(true)}
          disabled={images.length === 0} aria-label=""
        >
          <MdPhotoCamera />
        </IconButton>
        {images?.length !== 0 && <Badge 
          position='absolute'
          ml={-2}
          mt={6}
          zIndex={1}
          variant='solid'
          colorScheme="brand"
        >{images?.length}</Badge>}
      </Box>)}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title={title}
        headless
        footerless
        noscroll
        width={screen.width}
        
      >
        <Box maxH='100%' display='flex' flexDirection='column' __css={{
          '& div[class*="Carousel-root"]': {
            flex: '1',
          }
        }} pt={2} pb={2}
        >
          <Carousel
            responsive={{
              desktop: {
                breakpoint: { max: 3000, min: 1024 },
                items: 1,
                slidesToSlide: 1 // optional, default to 1.
              },
              tablet: {
                breakpoint: { max: 1024, min: 464 },
                items: 1,
                slidesToSlide: 1 // optional, default to 1.
              },
              mobile: {
                breakpoint: { max: 464, min: 0 },
                items: 1,
                slidesToSlide: 1 // optional, default to 1.
              }
            }}
            autoPlay={false}
            afterChange={onChange}
            swipeable={false}
          >
            {images.map((item, index) => item.src ?
                (<React.Fragment key={'image-' + item.image_pid + index}>
                  <PanZoomPicture src={item.src} alt={item.hash} />
                </React.Fragment>
              ) : (
                <>
                  <Skeleton
                    variant="rect"
                    width="100%"
                    height="70vh"
                    key={'skeleton-' + item.image_pid}
                  />
                </>
              )
            )}
          </Carousel>
          <Box display="flex" flexDirection="column">
            <Flex justifyContent="space-between">
              <Box display="flex" flexDirection="column">
                {extraInfo?.user && <FieldView title="Пользователь" value={extraInfo?.user} />}
                {(props.additional || null)}
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
              >
                {!!images[currentImage]?.last_update && (
                  <Txt
                    align="right"
                    display="block"
                    color="textSecondary"
                  >
                    {moment(images[currentImage].last_update).format('DD.MM.YYYY HH:mm')}
                  </Txt>
                )}
                </Box>
              </Flex>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={1}
          >
            <Button  
              variant='solid'
              size='sm'
              onClick={() => setOpen(false)}
            >
              <MdClose />
              <Box mr={1}>Закрыть</Box>
            </Button>
            {!!images[currentImage]?.hash && (<Box
              display="flex"
              alignItems="center"
              style={{ gap: 8 }}
              justifyContent="flex-end"
              flex={1}
            >
              {props.additional && <Button
                variant='outlined'
                size='small'
                
                onClick={() => {
                  setOpenExtra(true);
                }}
              >
                <Box mr={1}>Подробнее</Box>
                <MdInfo />
              </Button>}
              <Button
                variant='solid'
                size='sm'
                color='brand.500'
                onClick={() => {
                  shareToClipboard(images[currentImage].hash);
                }}
              >
                <Box mr={1}>Поделиться</Box>
                <MdShare />
              </Button>
            </Box>)}
          </Box>
        </Box>
      </Dialog>
      <Dialog title="Подробнее" open={openExtra} onClose={onClose}>
        {!!extraInfo && <Box
          display="flex"
          justifyContent="flex-start"
          flexDirection="column"
          alignItems="flex-start"
        >
          {extraInfo.visit_dt && (
            <FieldView
              title="Дата визита"
              value={moment(extraInfo.visit_dt).format(
                USER_DATE_TIME_FORMAT
              )}
              direction="row"
            />
          )}
          {extraInfo.outlet && (
            <FieldView
              title="Торговая точка/Склад"
              value={extraInfo.outlet.name}
              direction="row"
            />
          )}
          {extraInfo.full_address && (
            <FieldView
              title="Адрес"
              value={extraInfo.full_address}
              direction="row"
            />
          )}
          {extraInfo.user && (
            <FieldView
              title="Пользователь"
              value={extraInfo.user}
              direction="row"
            />
          )}
        </Box>}
      </Dialog>
    </>
  );
};

export default GalleryModal; 
